import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useLegalDocument } from '../../hooks/useLegalDocument';
import { getHostAgreementMe } from '../../services/getHostAgreementMe';
import { getUserAccessToken } from '../../services/userSession';
import './HostAgreementDocument.css';

export default function HostAgreementDocument({
    businessName,
    showEsignNotice = false,
    compact = false,
    personalized = false,
    authToken = null,
}) {
    const { doc: defaultDoc, loading: defaultLoading, error: defaultError } = useLegalDocument(
        personalized ? null : 'host_agreement'
    );
    const [personalDoc, setPersonalDoc] = useState(null);
    const [personalLoading, setPersonalLoading] = useState(personalized);
    const [personalError, setPersonalError] = useState(null);

    useEffect(() => {
        if (!personalized) {
            return undefined;
        }

        let cancelled = false;
        const token = authToken || getUserAccessToken();
        if (!token) {
            setPersonalError('Not signed in');
            setPersonalLoading(false);
            return undefined;
        }

        setPersonalLoading(true);
        setPersonalError(null);
        getHostAgreementMe(token)
            .then((res) => {
                if (cancelled) return;
                if (res.data?.success) {
                    setPersonalDoc({
                        title: res.data.title,
                        body: res.data.body,
                        version: res.data.version,
                        updatedAt: res.data.updatedAt,
                        esignNotice: res.data.esignNotice || null,
                        revenueSharePercent: res.data.revenueSharePercent,
                    });
                } else {
                    setPersonalError('Failed to load document');
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setPersonalError('Failed to load document');
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setPersonalLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [personalized, authToken]);

    const doc = personalized ? personalDoc : defaultDoc;
    const loading = personalized ? personalLoading : defaultLoading;
    const error = personalized ? personalError : defaultError;

    if (loading) {
        return (
            <article className={`host-agreement-document ${compact ? 'host-agreement-document--compact' : ''}`}>
                <p>Loading agreement…</p>
            </article>
        );
    }

    if (error || !doc) {
        return (
            <article className={`host-agreement-document ${compact ? 'host-agreement-document--compact' : ''}`}>
                <p>Could not load agreement. Please try again later.</p>
            </article>
        );
    }

    return (
        <article className={`host-agreement-document ${compact ? 'host-agreement-document--compact' : ''}`}>
            <header className="host-agreement-document-header">
                <h1>{doc.title}</h1>
                <p className="host-agreement-version">Version {doc.version}</p>
            </header>
            {businessName && (
                <p className="host-agreement-host-line">
                    <strong>Host:</strong> {businessName}
                </p>
            )}
            <div className="host-agreement-markdown">
                <ReactMarkdown>{doc.body}</ReactMarkdown>
            </div>
            {showEsignNotice && doc.esignNotice && (
                <p className="host-agreement-esign">{doc.esignNotice}</p>
            )}
        </article>
    );
}
